const _excluded = ["requiredFields"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import { ParamComponent } from 'panel';
import produce from 'immer';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { IronContextRegistry } from 'common/report/context-registry';
import { TemplateSpecs, TemplateType } from 'common/report/dashboards/types';
import { EntrySectionUpdates } from 'common/query-builder/query-entry-section/types';
import css from './index.inline.styl';
import { template } from './template';
import { ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP, RHSInputType, RHSPanelParams, RHSPanelProps, RHSPanelState, TemplateField, TutorialConfig } from './types';
export class RHSPanel extends ParamComponent<RHSPanelParams, RHSPanelState, IronContextRegistry> {
  constructor() {
    super(...arguments);
    // START: copied from boards-template-modal
    _defineProperty(this, "TEMPLATE_SELECTION_STEP", 0);
    _defineProperty(this, "EVENT_SELECTION_STEP", 1);
    _defineProperty(this, "WHAT_TO_TRACK_URL", "https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track");
    _defineProperty(this, "WHY_THESE_EVENTS_URL", "https://docs.mixpanel.com/docs/boards/templates#adding-your-events");
    _defineProperty(this, "CART_ANALYSIS_URL", "https://docs.mixpanel.com/docs/features/advanced#list-of-objects-property-support");
    _defineProperty(this, "VALUE_MOMENT_URL", "https://docs.mixpanel.com/docs/quickstart/what-to-track#step-2-track-a-value-moment-event");
    _defineProperty(this, "hasRequiredFields", (requiredFields: TemplateField[]): boolean => {
      return requiredFields.every(field => !!this.state.fields[field]);
    });
    _defineProperty(this, "TEMPLATE_CONFIGS", Object.keys(TemplateSpecs).reduce((acc, key) => {
      const _TemplateSpecs$key = TemplateSpecs[key],
        {
          requiredFields
        } = _TemplateSpecs$key,
        rest = _objectWithoutProperties(_TemplateSpecs$key, _excluded);
      return _objectSpread(_objectSpread({}, acc), {}, {
        [key]: _objectSpread(_objectSpread({}, rest), {}, {
          requiredFieldsCheck: () => this.hasRequiredFields(requiredFields)
        })
      });
    }, {}));
  }
  // END: copied from boards-template-modal
  get config() {
    return {
      contexts: ["currentProject" as const, "i18n" as const, "themes" as const],
      css,
      defaultState: {
        fields: {},
        calendarInputOpen: false,
        tutorialCurrentStepIndex: 0,
        showTutorial: false,
        hideTutorial: false
      },
      helpers: {
        getNoDataEventString: (): string => {
          switch (this.params.selectedTemplate) {
            case TemplateType.KPI:
            case TemplateType.FEATURE_LAUNCH:
              return "just two events";
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return "just one event";
            default:
              return "some events";
          }
        },
        getInfoEventString: (): string => {
          switch (this.params.selectedTemplate) {
            case TemplateType.E_COMMERCE:
              return this.t("boards.rhs.setup.ecommerce");
            case TemplateType.MARKETING_KPI:
            case TemplateType.KPI:
            case TemplateType.BENCHMARK_KPI:
              return this.t("boards.rhs.setup.two-events");
            case TemplateType.FEATURE_LAUNCH:
              return this.t("boards.rhs.setup.two-events-details");
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return this.t("boards.rhs.setup.one-event");
            case TemplateType.SAAS_COMPANY_KPI:
              return this.t("boards.rhs.setup.saas");
            default:
              return this.t("boards.rhs.setup.some-events");
          }
        },
        hasIntegratedArb: (): boolean | undefined => {
          var _this$getContext$get;
          return (_this$getContext$get = this.getContext("currentProject").get()) === null || _this$getContext$get === void 0 || (_this$getContext$get = _this$getContext$get.value) === null || _this$getContext$get === void 0 ? void 0 : _this$getContext$get.hasIntegratedArb;
        },
        updateField: (value: any, eventName: TemplateField, inputType: RHSInputType): void => {
          this.update(produce(this.state, draft => {
            draft.fields[eventName] = value;
          }));
          this.helpers.setTutorialStepIndex();
          this.dispatchEvent(new CustomEvent("updateBlueprint", {
            detail: {
              eventName,
              eventValue: value,
              inputType
            }
          }));
        },
        removeField: (eventName: TemplateField): void => {
          this.update(produce(this.state, draft => {
            delete draft.fields[eventName];
          }));
        },
        shouldDisableFinishButton: (): boolean | undefined => {
          var _this$TEMPLATE_CONFIG;
          return !((_this$TEMPLATE_CONFIG = this.TEMPLATE_CONFIGS[this.params.selectedTemplate]) !== null && _this$TEMPLATE_CONFIG !== void 0 && _this$TEMPLATE_CONFIG.requiredFieldsCheck()) || this.params.isLoading;
        },
        createTemplateBoard: () => {
          if (this.helpers.shouldDisableFinishButton()) {
            return;
          }
          this.dispatchEvent(new CustomEvent("commitBlueprint"));
        },
        startTutorial: (): void => {
          this.update({
            showTutorial: true
          });
        },
        dismissTutorial: (): void => {
          this.update({
            hideTutorial: true
          });
        },
        getTemplateTutorialConfig: (): TutorialConfig => {
          var _this$params;
          return ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP.get((_this$params = this.params) === null || _this$params === void 0 ? void 0 : _this$params.selectedTemplate);
        },
        setTutorialStepIndex: (): void => {
          const tutorial = this.helpers.getTemplateTutorialConfig();
          const {
            tutorialCurrentStepIndex
          } = this.state;
          if (tutorial) {
            let stepIndex = tutorialCurrentStepIndex;
            let [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            while (this.state.fields[templateField]) {
              // check each tutorial entry to see if it's field wasn't already filled out
              stepIndex = templateStep.nextStepIndex;
              [templateField, templateStep] = Object.entries(tutorial)[stepIndex];
            }
            this.update({
              tutorialCurrentStepIndex: stepIndex
            });
          }
        },
        handleEventUpdate: (updates: EntrySectionUpdates, eventName: TemplateField) => {
          if (updates.entries.length) {
            this.helpers.updateField(updates, eventName, RHSInputType.Event);
          } else {
            this.helpers.removeField(eventName);
          }
        },
        handleSeeAllTemplatesClick: () => {
          this.dispatchEvent(new CustomEvent("clickSeeAllTemplates", {
            detail: {
              fields: this.state.fields
            }
          }));
        },
        shouldShowReminderTooltip: () => {
          return this.params.showReminderTooltip;
        }
      },
      useShadowDom: true,
      params: {
        mode: String,
        selectedTemplate: String,
        isLoading: Boolean,
        isSeeAllTemplatesLoading: Boolean,
        showReminderTooltip: Boolean
      },
      template
    };
  }
  get t() {
    return this.getContext("i18n").t;
  }
  get print() {
    return this.getContext("i18n").print;
  }
}
export const RHSPanelEl = defineAndMakeJsxFactory<RHSPanelProps>("mp-rhs-panel", RHSPanel);
export const RHSPanelReact = panel2react<RHSPanelProps>("mp-rhs-panel", RHSPanel);